import { createActionsHook, createStateHook, createStore } from 'react-sweet-state';

export type MessageFeedbackType = 'like' | 'dislike';

type MessageFeedbackState = {
	feedbackSentIds: string[];
	feedbackMap: {
		[key: string]: MessageFeedbackType;
	};
};

const createFeedbackKey = (conversationId: string | undefined, messageId: string): string =>
	conversationId ? `${conversationId}:${messageId}` : messageId;

const store = createStore({
	initialState: {
		feedbackSentIds: [],
		feedbackMap: {},
	} as MessageFeedbackState,
	actions: {
		setFeedbackSent:
			(messageId: string, feedbackType?: MessageFeedbackType, conversationId?: string) =>
			({ setState, getState }) => {
				const state = getState();

				const newFeedbackSentIds = state.feedbackSentIds.includes(messageId)
					? state.feedbackSentIds
					: [...state.feedbackSentIds, messageId];

				let newFeedbackMap = state.feedbackMap;

				if (feedbackType && conversationId) {
					const key = createFeedbackKey(conversationId, messageId);
					newFeedbackMap = {
						...state.feedbackMap,
						[key]: feedbackType,
					};
				}

				setState({
					feedbackSentIds: newFeedbackSentIds,
					feedbackMap: newFeedbackMap,
				});
			},
	},
});

const useStoreActions = createActionsHook(store);
const useIsFeedbackSent = createStateHook(store, {
	selector: (state, messageId: string) => state.feedbackSentIds.includes(messageId),
});

const useFeedbackMap = createStateHook(store, {
	selector: (state) => state.feedbackMap,
});

export const useSetMessageFeedbackSent = (): ((
	messageId: string,
	feedbackType?: MessageFeedbackType | undefined,
	conversationId?: string | undefined,
) => void) => useStoreActions().setFeedbackSent;

export const useIsMessageFeedbackSent = (messageId: string): boolean =>
	useIsFeedbackSent(messageId);

export const useGetMessageFeedbackType = (
	messageId: string,
	conversationId?: string,
): MessageFeedbackType | undefined => {
	const feedbackMap = useFeedbackMap();
	const key = createFeedbackKey(conversationId, messageId);
	return feedbackMap[key];
};
